import useQueryGoldenSettingsConfig from '@hooks/goldenSettings/useQueryGoldenSettingsConfig';
import useQueryGoldenSettingsDates from '@hooks/goldenSettings/useQueryGoldenSettingsDates';
import useQueryGoldenSettingsInfo from '@hooks/goldenSettings/useQueryGoldenSettingsInfo';
import useQueryGoldenSettingsOnSelectedDate from '@hooks/goldenSettings/useQueryGoldenSettingsOnSelectedDate';
import useQueryGoldenSettingsSites from '@hooks/goldenSettings/useQueryGoldenSettingsSites';

import { formatDate } from '@utils/date';

import { ICMMavenirVendor } from '@interfaces/cmModule/cmModule';
import { TGoldenSettingsConfigTreeDTO, TGoldenSettingsSetTypeDTO } from '@interfaces/cmModule/dto/goldenSettingsDTO';

import useGoldenSettingsMutations, { IUseGoldenSettingsMutationsHandlers } from './useGoldenSettingsMutations';
import useQueryGoldenSettingsDatesMavenir from './useQueryGoldenSettingsDatesMavenir';
import useQueryGoldenSettingsOnSelectedDateMavenir from './useQueryGoldenSettingsOnSelectedDateMavenir';

type TUseGoldenSettingsProps = TGoldenSettingsSetTypeDTO
	& ICMMavenirVendor
	& TGoldenSettingsConfigTreeDTO
	& IUseGoldenSettingsMutationsHandlers

const useGoldenSettings = (params: TUseGoldenSettingsProps) => {
	const {
		vendor,
		technology,
		nodeType,
		siteId,
		date,
		path,
		nodes,
		type,
		version,
		isMavenirVendor,
		closeGoldenSettingForm,
	} = params;

	const { goldenSettingsDates, isLoadingGoldenSettingsDates } = useQueryGoldenSettingsDates({
		vendor,
		technology,
		nodeType,
		siteIds: siteId ? [siteId] : undefined,
	});

	const {
		goldenSettingsDatesMavenir,
		isLoadingGoldenSettingsDatesMavenir,
	} = useQueryGoldenSettingsDatesMavenir({
		vendor,
		nodes,
		type,
		version,
	});

	const { goldenSettingsSites, isLoadingGoldenSettingsSites } = useQueryGoldenSettingsSites({
		vendor,
		technology,
		nodeType,
	});

	const {
		goldenSettingsOnSelectedDate,
		isLoadingGoldenSettingsOnSelectedDate,
	} = useQueryGoldenSettingsOnSelectedDate({
		vendor,
		technology,
		nodeType,
		siteId,
		date,
	});

	const {
		goldenSettingsOnSelectedDateMavenir,
		rawConfigDataOnSelectedDate,
		isLoadingGoldenSettingsOnSelectedDateMavenir,
	} = useQueryGoldenSettingsOnSelectedDateMavenir({
		vendor,
		dateText: date ? formatDate(date) : undefined,
		nodes,
		path,
		type,
		version,
	});

	const { goldenSettingsInfo, isLoadingGoldenSettingsInfo } = useQueryGoldenSettingsInfo({
		vendor,
		technology,
		nodeType,
		nodes,
		type,
		version,
	});

	const { goldenSettingsConfig, isLoadingGoldenSettingsConfig } = useQueryGoldenSettingsConfig({
		vendor,
		technology,
		nodeType,
		nodes,
		type,
		version,
	});

	const {
		editGoldenSettingsConfig,
		setGoldenSettingsConfig,
		resetGoldenSettingsConfig,
		isLoadingMutations,
	} = useGoldenSettingsMutations({
		closeGoldenSettingForm,
		vendor,
		technology,
		nodeType,
	});

	return {
		goldenSettingsConfig,
		goldenSettingsInfo,
		rawConfigDataOnSelectedDate: isMavenirVendor ? rawConfigDataOnSelectedDate : goldenSettingsOnSelectedDate,
		goldenSettingsOnSelectedDate: isMavenirVendor
			? goldenSettingsOnSelectedDateMavenir
			: goldenSettingsOnSelectedDate,
		goldenSettingsDates: goldenSettingsDatesMavenir.length ? goldenSettingsDatesMavenir : goldenSettingsDates,
		goldenSettingsSites: goldenSettingsSites || [],
		editGoldenSettingsConfig,
		resetGoldenSettingsConfig,
		setGoldenSettingsConfig,
		isLoadingGoldenSettingsSites,
		isLoadingGoldenSettingsDates,
		isLoadingGoldenSettings: (
			isLoadingMutations
			|| isLoadingGoldenSettingsInfo
			|| isLoadingGoldenSettingsConfig
			|| isLoadingGoldenSettingsOnSelectedDate
			|| isLoadingGoldenSettingsDatesMavenir
			|| isLoadingGoldenSettingsOnSelectedDateMavenir
		),
	};
};

export default useGoldenSettings;
